import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Wrench, DollarSign, ChevronRight, Quote } from 'lucide-react';

// Hero media - using reliable CDN URLs
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/r312sf5m_P1050337.JPG';
const HERO_VIDEO = 'https://customer-assets.emergentagent.com/job_d8e02606-3ac6-451d-9b86-82235d5bbe57/artifacts/g6ug9o5e_att.DkunJ1XRT7uImT5rseNZAUQLOZsX-naC4L2ckl-WPXY%20%281%29.mp4';

// Images from CDN
const boatImages = [
  'https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/r312sf5m_P1050337.JPG',
  'https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/ff0a68ib_P1050334.JPG',
  'https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/hoq2jqcm_1000000381.jpg',
  'https://customer-assets.emergentagent.com/job_c1d7fe75-4e50-41d3-9936-35f1cf314c96/artifacts/mrqabnna_Copy%20of%2014%27.jpg',
];

const differentiators = [
  {
    icon: Shield,
    title: 'Superior Alloy',
    description: 'We use 5086 alloy on bottom plating with 27,000 PSI tensile strength in welded condition. Other manufacturers like Lund, SeaArk, Alweld, and G3 use 5052 with just 12,000 PSI.',
  },
  {
    icon: Wrench,
    title: 'Custom Built',
    description: 'We are a custom boat build shop. Change seating layout, add aluminum fuel tanks, or add something unique for your boating needs. We gladly accommodate you.',
  },
  {
    icon: DollarSign,
    title: 'Unbeatable Value',
    description: 'We build the strongest and most affordable boats. When you buy a boat, it should outlast your lifetime. Our boats are handcrafted to be passed on to the next generation.',
  },
];

const Home = () => {
  const [isDesktop, setIsDesktop] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    // Check if desktop on mount - video only plays on desktop for iOS compatibility
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 768);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section with Video Background (desktop) / Image (mobile) */}
      <section 
        className="relative flex items-center bg-slate-900" 
        style={{ minHeight: '90vh', minHeight: 'calc(100vh - 80px)' }}
        data-testid="hero-section"
      >
        {/* Background Video - Desktop Only */}
        {isDesktop && (
          <video
            autoPlay
            muted
            loop
            playsInline
            onLoadedData={() => setVideoLoaded(true)}
            className="absolute w-full h-full object-cover"
            style={{ top: 0, left: 0, opacity: videoLoaded ? 1 : 0, transition: 'opacity 0.5s ease' }}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        )}

        {/* Background Image - Always present as fallback, hidden when video loads on desktop */}
        <img 
          src={HERO_IMAGE}
          alt=""
          className="absolute w-full h-full object-cover"
          style={{ 
            top: 0, 
            left: 0, 
            opacity: (isDesktop && videoLoaded) ? 0 : 1,
            transition: 'opacity 0.5s ease'
          }}
        />
        
        {/* Overlay */}
        <div 
          className="absolute w-full h-full"
          style={{ 
            top: 0, 
            left: 0, 
            background: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.7) 0%, rgba(15, 23, 42, 0.5) 50%, rgba(15, 23, 42, 0.8) 100%)' 
          }}
        />
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-24">
          <div className="max-w-3xl">
            <p className="text-orange-500 font-heading text-lg uppercase tracking-widest mb-4">
              Alaska's Toughest Boats
            </p>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight mb-6">
              Built to Outlast You
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Commercial-grade aluminum boats handcrafted in Alaska. 
              Designed for the rigors of hunting, fishing, and everything in between.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/models" className="btn-primary flex items-center gap-2" data-testid="hero-cta-models">
                View Our Boats
                <ChevronRight className="w-5 h-5" />
              </Link>
              <Link to="/contact" className="btn-secondary" data-testid="hero-cta-contact">
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Banner */}
      <section className="bg-slate-900 py-16 md:py-20" data-testid="testimonial-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <Quote className="w-12 h-12 text-orange-500 mx-auto mb-6 opacity-60" />
            <blockquote className="text-xl md:text-2xl text-white leading-relaxed mb-6">
              "It is easy to launch solo, yet heavy enough to offer a solid ride in white caps. 
              The high sides and general stability mean we never have to worry about one of the kids 
              falling out when they reach out to land a fish. It is a solid, well built boat. 
              I will not hesitate to troll with it in Valdez Harbor next summer."
            </blockquote>
            <cite className="text-orange-500 font-heading text-lg uppercase tracking-wide">
              — John Klingel
            </cite>
          </div>
        </div>
      </section>

      {/* How We Are Different */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="differentiators-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
              Why Choose Us
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
              How We Are Different
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {differentiators.map((item, index) => (
              <div 
                key={item.title}
                className="bg-white border-2 border-slate-100 p-8 hover:border-orange-500/50 transition-all card-hover"
                data-testid={`differentiator-${index}`}
              >
                <div className="w-14 h-14 bg-orange-500 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Boat Preview Gallery */}
      <section className="py-24 md:py-32 bg-white" data-testid="preview-gallery-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
            <div>
              <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
                Our Fleet
              </p>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
                Built for Alaska
              </h2>
            </div>
            <Link 
              to="/gallery" 
              className="flex items-center gap-2 text-slate-900 font-heading font-bold uppercase tracking-wide hover:text-orange-500 transition-colors"
              data-testid="view-gallery-link"
            >
              View Full Gallery
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {boatImages.map((img, index) => (
              <div 
                key={index}
                className="gallery-item aspect-square overflow-hidden bg-slate-100"
                data-testid={`preview-image-${index}`}
              >
                <img 
                  src={img} 
                  alt={`Armageddon Boat ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-24 md:py-32" data-testid="cta-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build Your Dream Boat?
          </h2>
          <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
            Whether you need a heavy-duty river runner or a versatile landing craft, 
            we'll build it to your exact specifications.
          </p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="bottom-cta">
            Start Your Project
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
